'use client';

import { useEffect, useRef, useState } from 'react';

import { ChevronDown, UpArrowIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';

import {
  AccordionWrapper,
  AccordionContent,
  AccordionLabel,
  AccordionHeader,
  CustomIconWrapper,
} from './styled';
import { AccordionProps } from './props';

const Accordion = ({
  children,
  label,
  headerFullBorder = false,
  borderRadius,
  accordionContentBorderRadius,
  isStaticPage,
  borderColor = 'lightBlueGrey',
  headerPadding = '10px',
  backgroundColor,
  headerContentColor = 'white',
  bodyBackgroundColor,
  iconColor = 'white',
  defaultOpen = false,
  width = '100%',
  iconSize = 12,
  labelFontSize = '18px',
  mobileLabelFontSize = '14px',
  openedIcon = UpArrowIcon,
  closedIcon = ChevronDown,
  contentPadding = '10px',
  alwaysShowBottomBorder = true,
  showBottomBorderOnOpened = false,
  showBottomBorderOnClosed = false,
  showBodyBorder = false,
  differentColorWhenOpened = false,
  openedColor = 'white',
  labelWeight,
  showIconOnTop = false,
  closeOnOutsideClick = false,
  selectionCompleted = false,
  responsivePadding,
}: AccordionProps) => {
  const accordionRef = useRef(null);

  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  useEffect(() => {
    setIsOpen(false);
  }, [selectionCompleted]);

  useEffect(() => {
    setIsOpen(defaultOpen);
  }, [defaultOpen]);

  const toggleAccordion = () => {
    setIsOpen(!isOpen);
  };
  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (
        accordionRef.current &&
        (accordionRef.current as HTMLElement).contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (closeOnOutsideClick && isOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [closeOnOutsideClick, isOpen]);
  return (
    <AccordionWrapper width={width} ref={accordionRef}>
      <AccordionHeader
        showIconOnTop={showIconOnTop}
        headerFullBorder={headerFullBorder}
        alwaysShowBottomBorder={
          alwaysShowBottomBorder
            ? true
            : showBottomBorderOnOpened
            ? isOpen
              ? true
              : false
            : showBottomBorderOnClosed
            ? isOpen
              ? false
              : true
            : false
        }
        borderRadius={
          isStaticPage ? '8px' : isOpen ? '8px 8px 0px 0px' : borderRadius
        }
        headerPadding={headerPadding}
        backgroundColor={
          differentColorWhenOpened
            ? isOpen
              ? openedColor
              : backgroundColor
            : backgroundColor
        }
        headerContentColor={headerContentColor}
        borderColor={borderColor}
        onClick={toggleAccordion}
        responsivePadding={responsivePadding}
      >
        <AccordionLabel
          labelWeight={labelWeight}
          labelFontSize={labelFontSize}
          mobileLabelFontSize={mobileLabelFontSize}
        >
          {label}
        </AccordionLabel>

        <CustomIconWrapper>
          <CustomIcon
            icon={isOpen ? openedIcon : closedIcon}
            fontSize={iconSize}
            paletteColor={iconColor}
          />
        </CustomIconWrapper>
      </AccordionHeader>
      {isOpen ? (
        <AccordionContent
          backgroundColor={
            bodyBackgroundColor
              ? differentColorWhenOpened
                ? isOpen
                  ? openedColor
                  : bodyBackgroundColor
                : bodyBackgroundColor
              : differentColorWhenOpened
              ? isOpen
                ? openedColor
                : backgroundColor
              : backgroundColor
          }
          contentPadding={contentPadding}
          isOpen={isOpen}
          showBodyBorder={showBodyBorder}
          borderColor={borderColor}
          borderRadius={
            accordionContentBorderRadius
              ? '0'
              : isOpen
              ? '0px 0px 8px 8px'
              : borderRadius
          }
        >
          {children}
        </AccordionContent>
      ) : null}
    </AccordionWrapper>
  );
};

export default Accordion;
